import { movies, type Movie } from "@/mocks/movies";

export const ALL_GENRES = Array.from(
  new Set(movies.flatMap((m) => m.genres))
).sort((a, b) => a.localeCompare(b, "zh-Hans-CN"));

export const ALL_YEARS = Array.from(new Set(movies.map((m) => m.year))).sort(
  (a, b) => b - a
);

export type SortKey = "latest" | "popular" | "rating";

export const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "latest", label: "最新更新" },
  { value: "popular", label: "最多点击" },
  { value: "rating", label: "评分最高" },
];

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((m) => m.id === id);
}

export interface FilterParams {
  genres?: string[];
  year?: string;
  minRating?: number;
  sort?: SortKey;
}

export function filterMovies(
  source: Movie[],
  params: FilterParams = {}
): Movie[] {
  const { genres = [], year = "all", minRating = 0, sort = "latest" } = params;
  let result = source.filter((m) => {
    const genreOk =
      genres.length === 0 || genres.some((g) => m.genres.includes(g));
    const yearOk = year === "all" || String(m.year) === year;
    const ratingOk = m.rating >= minRating;
    return genreOk && yearOk && ratingOk;
  });

  result = [...result].sort((a, b) => {
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "popular") return b.views - a.views;
    return b.year - a.year || b.rating - a.rating;
  });

  return result;
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  return movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      const genreScore = m.genres.filter((g) => movie.genres.includes(g)).length;
      const themeScore = m.theme === movie.theme ? 2 : 0;
      const tagScore = m.tags.filter((t) => movie.tags.includes(t)).length;
      return { m, score: genreScore * 2 + themeScore + tagScore };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.m);
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.year - a.year).slice(0, limit);
}

export function getPopularMovies(limit = 6): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getMoviesByType(type: "movie" | "tv"): Movie[] {
  return movies.filter((m) => m.type === type);
}

export function getTopicMovies(): Movie[] {
  return movies.filter((m) => m.theme === "年轻的母亲");
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((m) => {
    return (
      m.title.toLowerCase().includes(q) ||
      m.genres.some((g) => g.toLowerCase().includes(q)) ||
      m.tags.some((t) => t.toLowerCase().includes(q)) ||
      m.theme.toLowerCase().includes(q) ||
      m.director.toLowerCase().includes(q) ||
      m.cast.toLowerCase().includes(q) ||
      m.summary.toLowerCase().includes(q)
    );
  });
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return String(views);
}

export const CATEGORY_META: Record<
  string,
  { title: string; description: string; kind: "type" | "topic" }
> = {
  movie: {
    title: "电影",
    description:
      "精选电影资源合集，涵盖剧情、家庭、悬疑、科幻等多元类型，快速查看剧情介绍与详情。",
    kind: "type",
  },
  tv: {
    title: "电视剧",
    description:
      "热播电视剧资源合集，都市、家庭、古装、悬疑应有尽有，随时浏览剧集介绍。",
    kind: "type",
  },
  topic: {
    title: "主题专题",
    description:
      "围绕“年轻的母亲”等温暖主题策划的影视专题合集，聚焦情感与亲情的动人故事。",
    kind: "topic",
  },
};